
import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar } from "@/components/ui/avatar";
import { Star } from "lucide-react";
import { TripRating } from "@/services/ratingService";
import { formatDistanceToNow } from "date-fns";

interface RatingDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ratings?: TripRating[];
  averageRating?: number;
  totalReviews?: number;
  distribution?: { stars: number; percentage: number }[];
}

const RatingDetailDialog = ({
  open,
  onOpenChange,
  ratings = [],
  averageRating = 0,
  totalReviews = 0,
  distribution = [],
}: RatingDetailDialogProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] p-0 gap-0 overflow-hidden bg-white text-gray-800">
        <DialogHeader className="p-6 pb-2">
          <DialogTitle className="text-xl font-bold text-gray-900">My Ratings</DialogTitle>
        </DialogHeader>
        
        <div className="p-6 pt-0 pb-3">
          <p className="text-sm text-gray-600">
            Rating are verified and are from people who use the DISCO service.
          </p>
        </div>

        <div className="flex px-6 items-start">
          <div className="flex flex-col items-center pr-6">
            <div className="text-6xl font-bold text-gray-900 leading-none">
              {averageRating.toFixed(1)}
            </div>
            <div className="flex mt-1">
              {[...Array(5)].map((_, i) => (
                <Star 
                  key={i} 
                  size={16} 
                  className={
                    i < Math.floor(averageRating) 
                      ? "text-neon-green fill-neon-green" 
                      : i < averageRating 
                        ? "text-neon-green fill-neon-green opacity-60" 
                        : "text-gray-300"
                  } 
                />
              ))}
            </div>
            <div className="text-sm text-gray-600 mt-1">
              {totalReviews}
            </div>
          </div>
          
          <div className="flex-1 space-y-1.5 py-1">
            {distribution.map((item) => (
              <div key={item.stars} className="flex items-center">
                <span className="w-6 text-sm font-medium text-gray-700">{item.stars}</span>
                <div className="flex-1 mx-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-neon-green rounded-full"
                    style={{ width: `${item.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <ScrollArea className="h-[320px] mt-4 px-6 pb-6">
          <div className="space-y-6">
            {ratings.map((rating) => (
              <div key={rating.id} className="flex gap-4">
                <Avatar className="w-10 h-10 bg-gray-100 shrink-0 border border-gray-200">
                  <span className="text-gray-600">U</span>
                </Avatar>
                <div className="flex-1">
                  <div className="flex justify-between">
                    <h4 className="font-medium text-gray-900">User Name</h4>
                    <span className="text-xs text-gray-500">
                      {formatDistanceToNow(new Date(rating.created), { addSuffix: true })}
                    </span>
                  </div>
                  <div className="flex text-neon-green mt-0.5 mb-1">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        size={14}
                        className={i < rating.stars ? "fill-neon-green" : "text-gray-300"} 
                      />
                    ))}
                  </div>
                  <p className="text-sm text-gray-700">
                    {rating.review}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default RatingDetailDialog;
